import { Tables, TablesInsert, TablesUpdate } from "@/types/database.types";
import { supabase } from "@/integrations/supabase/client";

export type Folder = Tables<"folders">;
export type FolderInsert = TablesInsert<"folders">;
export type FolderUpdate = TablesUpdate<"folders">;

export interface FolderTreeNode extends Omit<Folder, "updated_at"> {
  depth: number;
  path: string[];
  output_count: number;
}

export const folderService = {
  async getFolderTree(
    organizationId: string,
    includeArchived = false,
  ): Promise<{ data: FolderTreeNode[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.rpc("get_folder_tree", {
        org_id: organizationId,
        include_archived: includeArchived,
      });

      if (error) return { data: null, error: new Error(error.message) };
      return { data: (data as unknown as FolderTreeNode[]) || [], error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async createFolder(
    input: Omit<FolderInsert, "id" | "created_at" | "updated_at">,
  ): Promise<{ data: Folder | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("folders")
        .insert(input)
        .select("*")
        .single();
      if (error) return { data: null, error: new Error(error.message) };
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async renameFolder(
    folderId: string,
    newName: string,
  ): Promise<{ data: Folder | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("folders")
        .update({ name: newName })
        .eq("id", folderId)
        .select("*")
        .single();
      if (error) return { data: null, error: new Error(error.message) };
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async updateFolder(
    folderId: string,
    updates: Partial<
      Pick<FolderUpdate, "name" | "folder_color" | "is_private" | "sort_order">
    >,
  ): Promise<{ data: Folder | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("folders")
        .update(updates as any)
        .eq("id", folderId)
        .select("*")
        .single();
      if (error) return { data: null, error: new Error(error.message) };
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async moveFolder(
    folderId: string,
    newParentId: string | null,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { data, error } = await supabase.rpc("move_folder", {
        folder_id: folderId,
        new_parent_id: newParentId,
      });
      if (error) return { success: false, error: new Error(error.message) };
      return { success: true, error: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async archiveFolder(
    folderId: string,
    archive = true,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await supabase.rpc("archive_folder", {
        folder_id: folderId,
        archive_state: archive,
      });
      if (error) return { success: false, error: new Error(error.message) };
      return { success: true, error: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async deleteFolder(
    folderId: string,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await supabase
        .from("folders")
        .delete()
        .eq("id", folderId);
      if (error) return { success: false, error: new Error(error.message) };
      return { success: true, error: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async listFolderChildren(
    organizationId: string,
    parentFolderId: string | null,
  ): Promise<{ data: Folder[] | null; error: Error | null }> {
    try {
      let query = supabase
        .from("folders")
        .select("*")
        .eq("organization_id", organizationId)
        .order("sort_order")
        .order("name");
      query = parentFolderId
        ? query.eq("parent_folder_id", parentFolderId)
        : query.is("parent_folder_id", null);
      const { data, error } = await query;
      if (error) return { data: null, error: new Error(error.message) };
      return { data: data || [], error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },
};
